/**
 * TrainersPage - Olympic Fit Wellness
 * Connected to live Supabase backend for trainer profiles
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Award,
  Calendar,
  Star,
  MapPin,
  Mail,
  Loader2
} from 'lucide-react';
import api from '../lib/api';

export default function TrainersPage() {
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [loading, setLoading] = useState(true);
  const [trainers, setTrainers] = useState([]);

  const specialties = [
    { id: 'all', name: 'All Team' },
    { id: 'founders', name: 'Founders' },
    { id: 'recovery', name: 'Recovery & Wellness' },
  ];

  // OFW Team Members - Real trainers only
  const defaultTrainers = [
    // FOUNDERS
    {
      id: 'rae-edwards',
      name: 'Monzavous "Rae" Edwards',
      title: 'Co-Founder & Elite Performance Coach',
      specialty: 'founders',
      image: 'https://customer-assets.emergentagent.com/job_169912b9-09da-4c66-9ed7-f663c1e945eb/artifacts/cme3inmy_image.png',
      certifications: ['Olympic Games Athlete', 'World Championships', 'Pan American Games', 'USA Junior Champion'],
      experience: '20+ years',
      bio: 'World-class sprinter with personal bests of 10.00s (100m) and 20.17s (200m). From USA Junior Champion to the Olympic Games, Rae brings elite athletic discipline to every training session.',
      extendedBio: 'Born in 1991 in Opelika, Alabama, Rae first broke through in 2000 by winning both the USA Junior and Junior College Championships. Over the next decade, he steadily honed his craft and cemented his place among the world\'s top sprinters, reaching the final of the USA Outdoor Championships and earning qualification to his first senior World Championships.',
      location: 'All Locations'
    },
    {
      id: 'clint-fuqua',
      name: 'Clint Fuqua',
      title: 'Co-Founder & The Health Engineer',
      specialty: 'founders',
      image: 'https://customer-assets.emergentagent.com/job_169912b9-09da-4c66-9ed7-f663c1e945eb/artifacts/vrucwbkt_image.png',
      certifications: ['Certified Health Coach', 'Certified Personal Trainer', 'Nutrition Specialist', 'Corporate Wellness Expert'],
      experience: '25+ years',
      bio: '"The Health Engineer" - A highly sought-after certified health coach and personal trainer serving individuals, families, and companies throughout North Dallas.',
      extendedBio: 'Clint\'s philosophy is built on personalization and sustainability: he designs programs that fit a person\'s actual life, not just an ideal schedule on paper. He is known for his ability to translate complex topics like biomechanics, metabolic health, and supplementation into clear, practical action steps that anyone can follow.',
      location: 'All Locations'
    },
    // TEAM MEMBERS
    {
      id: 'angelo-keyes',
      name: 'Angelo Keyes',
      title: 'CEO, O3Zone Sauna Therapy Services Dallas',
      specialty: 'recovery',
      image: 'https://customer-assets.emergentagent.com/job_3706acf2-5e9b-4738-8722-c58b1cce635b/artifacts/i6jlt8lv_image.png',
      certifications: ['Sports Recovery Specialist', 'Performance Coach', 'O3Zone Certified', 'Bold Warrior Inc. Founder'],
      experience: '15+ years',
      bio: 'CEO of O3Zone Sauna Therapy Services Dallas and Founder/CEO of Bold Warrior Inc. A board member of the Watering Seeds Concussion Awareness Group (CAMP) and a recognized expert in sports recovery and alternative therapies.',
      extendedBio: 'Angelo has dedicated his career to helping athletes, first responders, and military personnel optimize performance and recovery through holistic, science-backed solutions. He also launched Opti-Surge.com, a premier men\'s health brand specializing in testosterone optimization and metabolic enhancement.',
      location: 'McKinney'
    },
    {
      id: 'dr-chris',
      name: 'Dr. Chris',
      title: 'Holistic Wellness Consultant & Founding Member',
      specialty: 'recovery',
      image: 'https://customer-assets.emergentagent.com/job_3706acf2-5e9b-4738-8722-c58b1cce635b/artifacts/f0ckl1ur_image.png',
      certifications: ['D.O.', 'FACOS', 'ABIHM', 'DAASM', 'CWSP', 'CDP', 'CALM'],
      experience: '25+ years',
      bio: 'Holistic healing strategist with 25+ years experience across conventional and holistic health. Founding trustee of WAHPEP (Wellness and Healing Place for Exceptional People) and co-founder of PPWCOA.',
      extendedBio: 'Dr. Chris works alongside gym members, athletes, and high performers to support energy, hydration, recovery, and resilience through personalized, non-invasive wellness strategies, including IV wellness services. His guiding philosophy is YOUNGEVITY—living younger, longer.',
      location: 'All Locations',
      social: { facebook: 'https://www.facebook.com/WAHPEPCollective/' }
    }
  ];

  useEffect(() => {
    fetchTrainers();
  }, []);

  const fetchTrainers = async () => {
    try {
      setLoading(true);
      // Try to fetch from API
      const [profiles, apiTrainers] = await Promise.all([
        api.getTrainerProfiles().catch(() => []),
        api.getTrainers().catch(() => [])
      ]);

      // Merge API data with default trainers
      let mergedTrainers = [...defaultTrainers];
      
      // Add any trainers from profiles
      if (profiles && profiles.length > 0) {
        profiles.forEach(profile => {
          const exists = mergedTrainers.find(t => 
            t.name.toLowerCase() === profile.full_name?.toLowerCase()
          );
          if (!exists) {
            mergedTrainers.push({
              id: profile.trainer_id,
              name: profile.full_name,
              title: profile.title || profile.role,
              specialty: 'recovery',
              image: profile.avatar_url,
              certifications: profile.certifications || [],
              experience: 'Expert',
              bio: profile.bio,
              location: 'All Locations'
            });
          }
        });
      }

      setTrainers(mergedTrainers);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      setTrainers(defaultTrainers);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrainers = selectedSpecialty === 'all' 
    ? trainers 
    : trainers.filter(t => t.specialty === selectedSpecialty);

  return (
    <div className="bg-white" data-testid="trainers-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1920&q=80"
            alt="OFW Trainers"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative z-10 container mx-auto px-6 lg:px-12 pt-32">
          <div className="max-w-3xl">
            <span className="text-sm font-heading tracking-[0.15em] text-white/70 mb-6 block">EXPERT GUIDANCE</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-normal text-white tracking-[0.04em] uppercase mb-6">
              Our Trainers
            </h1>
            <p className="text-white/70 max-w-xl text-base tracking-wide leading-relaxed">
              Meet the dedicated professionals who will guide you on your wellness journey. Each trainer brings unique expertise and a passion for helping you achieve your goals.
            </p>
          </div>
        </div>
      </section>

      {/* Trainers Grid Section */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-20">
            {specialties.map((specialty) => (
              <button
                key={specialty.id}
                onClick={() => setSelectedSpecialty(specialty.id)}
                className={`px-8 py-4 text-sm font-heading tracking-[0.08em] transition-all duration-300 ${
                  selectedSpecialty === specialty.id
                    ? 'bg-black text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {specialty.name.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Trainers Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-1">
            {filteredTrainers.map((trainer) => (
              <div 
                key={trainer.id}
                className="group bg-white border border-neutral-200 hover:border-black transition-colors duration-300"
                data-testid={`trainer-${trainer.id}`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img 
                    src={trainer.image}
                    alt={trainer.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-normal text-neutral-900 tracking-wide mb-1">
                    {trainer.name}
                  </h3>
                  <p className="text-sm text-neutral-500 tracking-wider uppercase mb-4">
                    {trainer.title}
                  </p>
                  <p className="text-base text-neutral-600 mb-4 line-clamp-2 tracking-wide">
                    {trainer.bio}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-neutral-500 mb-4">
                    <MapPin className="w-4 h-4" />
                    <span className="tracking-wide">{trainer.location}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {trainer.certifications.slice(0, 2).map((cert, idx) => (
                      <span 
                        key={idx}
                        className="text-xs tracking-wider px-3 py-1 bg-neutral-100 text-neutral-600 uppercase"
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                  <Link 
                    to={`/trainers/${trainer.id}`}
                    className="inline-flex items-center gap-2 text-sm font-heading tracking-[0.08em] text-neutral-900 hover:text-neutral-600 transition-colors"
                  >
                    VIEW PROFILE <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Team Section */}
      <section className="py-24 lg:py-32 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-6 block">CAREERS</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-white tracking-[0.04em] uppercase mb-8">
                Join Our Team
              </h2>
              <p className="text-neutral-400 mb-8 tracking-wide leading-relaxed text-base">
                Are you a passionate fitness professional looking to make a difference? We're always seeking talented trainers, instructors, and wellness experts to join the OFW family.
              </p>
              <ul className="space-y-4 mb-10">
                {[
                  'Competitive compensation & benefits',
                  'Continuing education opportunities',
                  'State-of-the-art facilities',
                  'Supportive team environment',
                  'Career growth pathways'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-4 text-neutral-300 text-base tracking-wide">
                    <Award className="w-5 h-5 text-white" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/careers"
                className="inline-flex items-center gap-3 bg-white text-black font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-100 transition-all duration-300"
              >
                VIEW OPEN POSITIONS <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="aspect-square overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800"
                alt="OFW Team"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Book a Session CTA */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-6 block">GET STARTED</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-neutral-900 tracking-[0.04em] uppercase mb-6">
            Ready to Begin?
          </h2>
          <p className="text-neutral-500 max-w-xl mx-auto mb-10 tracking-wide text-base">
            Book a complimentary consultation with one of our expert trainers and take the first step toward your wellness goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center gap-3 bg-black text-white font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-all duration-300"
            >
              <Calendar className="w-5 h-5" />
              BOOK CONSULTATION
            </Link>
            <Link 
              to="/membership"
              className="inline-flex items-center justify-center gap-3 border border-black text-black font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-black hover:text-white transition-all duration-300"
            >
              VIEW MEMBERSHIP OPTIONS
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
